/**
 * Where a piece of cultural knowledge came from, stated plainly wherever it appears.
 *
 * The distinction that matters most: a machine's words must never be able to
 * pass as an elder's. Four separate things a family should always be able to
 * tell apart — her own voice, the family's telling, a documented source, and a
 * machine's draft — plus who the family allows it to reach.
 */
import { BookMarked, Bot, Handshake, Lock, Mic, Users, type LucideIcon } from 'lucide-react';

const PROVENANCE: Record<string, { label: string; Icon: LucideIcon; tone: string; title: string }> = {
  family_recording: { label: 'In their own voice', Icon: Mic, tone: 'voice', title: 'Recorded by the family — this is the person speaking' },
  family_told: { label: 'Family telling', Icon: Users, tone: '', title: 'Written down by the family from memory' },
  documented_source: { label: 'Documented source', Icon: BookMarked, tone: '', title: 'Taken from a source the family named' },
  community_contributed: { label: 'Community', Icon: Handshake, tone: '', title: 'Contributed by a speaker outside this family' },
  ai_generated: { label: 'Drafted by AI', Icon: Bot, tone: 'machine', title: 'Written by a machine and awaiting a native speaker — not anyone in your family' },
};

const LICENCE: Record<string, string> = {
  family_only: 'This family only',
  extended_family: 'Extended family',
  community_licensed: 'Shared with the language community',
  educational_use: 'Schools may use it',
  research_permitted: 'Researchers may use it',
  public_domain: 'Given to everyone',
};

const SENSITIVITY: Record<string, { label: string; tone: string; title: string }> = {
  adults_only: { label: 'Adults only', tone: 'restricted', title: 'Kept from children and teenagers' },
  restricted: { label: 'Restricted', tone: 'restricted', title: 'Kept from children, teenagers and every AI provider' },
};

export function ProvenanceTag({ provenance, aiAssisted }: { provenance?: string | null; aiAssisted?: boolean }) {
  const key = provenance ?? 'family_recording';
  const p = PROVENANCE[key] ?? PROVENANCE.family_recording!;
  return (
    <span className={`provenance ${p.tone}`} title={p.title}>
      <p.Icon size={14} strokeWidth={1.9} aria-hidden="true" />
      {p.label}
      {aiAssisted && key !== 'ai_generated' && <span className="muted"> · AI helped with the words</span>}
    </span>
  );
}

export function SensitivityTag({ sensitivity }: { sensitivity?: string | null }) {
  if (!sensitivity || sensitivity === 'ordinary') return null;
  const s = SENSITIVITY[sensitivity];
  if (!s) return null;
  return (
    <span className={`provenance ${s.tone}`} title={s.title}>
      <Lock size={14} strokeWidth={1.9} aria-hidden="true" />
      {s.label}
    </span>
  );
}

export function LicenceTag({ licence }: { licence?: string | null }) {
  if (!licence) return null;
  return <span className="provenance" title="Who your family allows this to reach">{LICENCE[licence] ?? licence}</span>;
}

export const LICENCE_OPTIONS = Object.entries(LICENCE).map(([key, label]) => ({ key, label }));
